'use client'

import { useState } from 'react'
import Image from 'next/image'
import { Button } from './shared/button'

interface FormData {
  firstName: string
  lastName: string
  phoneNumber: string
  email: string
  subject: string
  message: string
}

interface FormErrors {
  firstName?: string
  lastName?: string
  phoneNumber?: string
  email?: string
  subject?: string
  message?: string
}

export const ContactForm = () => {
  const [formData, setFormData] = useState<FormData>({
    firstName: '',
    lastName: '',
    phoneNumber: '',
    email: '',
    subject: '',
    message: '',
  })

  const [errors, setErrors] = useState<FormErrors>({})
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isSuccess, setIsSuccess] = useState(false)

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {}

    if (!formData.firstName.trim())
      newErrors.firstName = 'First name is required'
    if (!formData.lastName.trim()) newErrors.lastName = 'Last name is required'
    if (!formData.phoneNumber.trim())
      newErrors.phoneNumber = 'Phone number is required'
    if (!formData.email.trim()) newErrors.email = 'Email is required'
    if (!formData.subject.trim()) newErrors.subject = 'Subject is required'
    if (!formData.message.trim()) newErrors.message = 'Message is required'

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const resetForm = () => {
    setFormData({
      firstName: '',
      lastName: '',
      phoneNumber: '',
      email: '',
      subject: '',
      message: '',
    })
    setErrors({})
  }

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target
    setFormData((prev) => ({ ...prev, [name]: value }))

    // Clear error when user starts typing
    if (errors[name as keyof FormErrors]) {
      setErrors((prev) => ({ ...prev, [name]: undefined }))
    }
  }

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()

    if (!validateForm()) return

    setIsSubmitting(true)
    setIsSuccess(false)

    try {
      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      })

      if (response.ok) {
        setIsSuccess(true)
        resetForm()
        setTimeout(() => setIsSuccess(false), 5000) // Hide success message after 5 seconds
      } else {
        throw new Error('Failed to send message')
      }
    } catch (error) {
      console.error('Error submitting form:', error)
      // You could add error state here if needed
    } finally {
      setIsSubmitting(false)
    }
  }
  return (
    <div className="space-y-4">
      {isSuccess && (
        <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded">
          Your message has been sent successfully! We&apos;ll get back to you
          soon.
        </div>
      )}

      <form
        onSubmit={handleSubmit}
        className="space-y-4 [&_input]:bg-gray-50 [&_input]:rounded [&_input]:p-4 [&_input]:placeholder:text-gray-400 [&_input]:border [&_input]:border-gray-200 [&_textarea]:bg-gray-50 [&_textarea]:rounded [&_textarea]:p-4 [&_textarea]:placeholder:text-gray-400 [&_textarea]:border [&_textarea]:border-gray-200"
      >
        <div className="flex flex-wrap gap-4">
          <div className="w-full">
            <input
              type="text"
              name="firstName"
              placeholder="First Name"
              className={`w-full ${errors.firstName ? 'border-red-500' : ''}`}
              value={formData.firstName}
              onChange={handleInputChange}
            />
            {errors.firstName && (
              <p className="text-red-500 text-sm mt-1">{errors.firstName}</p>
            )}
          </div>
          <div className="w-full">
            <input
              type="text"
              name="lastName"
              placeholder="Last Name"
              className={`w-full ${errors.lastName ? 'border-red-500' : ''}`}
              value={formData.lastName}
              onChange={handleInputChange}
            />
            {errors.lastName && (
              <p className="text-red-500 text-sm mt-1">{errors.lastName}</p>
            )}
          </div>
        </div>

        <div className="flex flex-wrap gap-4">
          <div className="w-full">
            <input
              type="text"
              name="phoneNumber"
              placeholder="Phone Number"
              className={`w-full ${errors.phoneNumber ? 'border-red-500' : ''}`}
              value={formData.phoneNumber}
              onChange={handleInputChange}
            />
            {errors.phoneNumber && (
              <p className="text-red-500 text-sm mt-1">{errors.phoneNumber}</p>
            )}
          </div>
          <div className="w-full">
            <input
              type="email"
              name="email"
              placeholder="Your Email"
              className={`w-full ${errors.email ? 'border-red-500' : ''}`}
              value={formData.email}
              onChange={handleInputChange}
            />
            {errors.email && (
              <p className="text-red-500 text-sm mt-1">{errors.email}</p>
            )}
          </div>
        </div>

        <div>
          <input
            type="text"
            name="subject"
            placeholder="Subject"
            className={`w-full ${errors.subject ? 'border-red-500' : ''}`}
            value={formData.subject}
            onChange={handleInputChange}
          />
          {errors.subject && (
            <p className="text-red-500 text-sm mt-1">{errors.subject}</p>
          )}
        </div>

        <div>
          <textarea
            name="message"
            placeholder="Your Message"
            className={`w-full h-40 ${errors.message ? 'border-red-500' : ''}`}
            value={formData.message}
            onChange={handleInputChange}
          />
          {errors.message && (
            <p className="text-red-500 text-sm mt-1">{errors.message}</p>
          )}
        </div>

        <div className="flex flex-wrap justify-between mt-10 gap-4">
          <div className="flex gap-4 [&_img]:h-5">
            <Image src="/logo_stripe.png" alt="stripe" width={48} height={20} />
            <Image
              src="/MystiflyLogo.png"
              alt="mystifly"
              width={82}
              height={20}
            />
          </div>
          <Button
            type="submit"
            className="w-full lg:w-auto"
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Sending...' : 'Send Message'}
          </Button>
        </div>
      </form>
    </div>
  )
}
